import { useState } from "react";
import { Briefcase, Check, Shield, CreditCard, Clock, TrendingUp, UserCheck, FileText } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { EMIRATES } from "../lib/i18n";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SubmitBar } from "../components/SubmitBar";

const BENEFIT_ICONS = [CreditCard, UserCheck, TrendingUp, Clock, Shield, FileText];

export default function BusinessAccount() {
  const { t, lang } = useLang();
  const b = t.businessAccount;

  const [form, setForm] = useState({
    company_name: "",
    trade_license: "",
    vat_number: "",
    business_type: "restaurant_group",
    emirate: "dubai",
    contact_name: "",
    contact_role: "",
    phone: "",
    email: "",
    monthly_volume: "under_20k",
    credit_terms: "prepaid",
    authorized_signatory: "",
    additional_notes: "",
  });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const payload = {
    name: form.contact_name || form.company_name,
    phone: form.phone,
    project_type: "restaurant",
    emirate: form.emirate,
    message: `Business Account Application from ${form.company_name}`,
    company_name: form.company_name,
    email: form.email,
    preferred_contact: "email",
  };
  const extra = {
    trade_license: form.trade_license,
    vat_number: form.vat_number,
    business_type: form.business_type,
    contact_role: form.contact_role,
    monthly_volume: form.monthly_volume,
    credit_terms: form.credit_terms,
    authorized_signatory: form.authorized_signatory,
    additional_notes: form.additional_notes,
  };

  return (
    <div data-testid="business-account-page">
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#0f172a] via-[#0891b2] to-[#0f172a] text-white overflow-hidden">
        <div className="absolute top-0 end-0 w-[500px] h-[500px] rounded-full bg-[#ef4444]/10 blur-3xl pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.35em] text-red-300 font-semibold mb-4 uppercase">
                <Briefcase className="w-3.5 h-3.5" />
                {b.eyebrow}
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-5">
                {b.title}
              </h1>
              <p className="text-cyan-100 text-lg leading-relaxed mb-8">{b.subtitle}</p>
              <div className="flex flex-wrap gap-3 text-[11px] uppercase tracking-widest text-cyan-200">
                <span className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 rounded-full px-3 py-1.5">
                  <Check className="w-3 h-3 text-green-400" /> {b.chip1}
                </span>
                <span className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 rounded-full px-3 py-1.5">
                  <Check className="w-3 h-3 text-green-400" /> {b.chip2}
                </span>
                <span className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 rounded-full px-3 py-1.5">
                  <Check className="w-3 h-3 text-green-400" /> {b.chip3}
                </span>
              </div>
            </div>
            <div className="hidden lg:flex justify-center">
              <div className="relative w-[380px] h-[380px]">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#ef4444]/30 to-[#0891b2]/30 blur-2xl" />
                <div className="absolute inset-6 rounded-3xl bg-white/5 border border-white/10 backdrop-blur flex items-center justify-center">
                  <Briefcase className="w-40 h-40 text-white/20" strokeWidth={0.5} />
                </div>
                <div className="absolute top-8 end-8 bg-white text-slate-800 rounded-xl px-4 py-3 shadow-2xl">
                  <div className="text-[10px] text-slate-500 uppercase tracking-widest">{b.card1Label}</div>
                  <div className="font-display text-lg text-[#0891b2]">{b.card1Value}</div>
                </div>
                <div className="absolute bottom-8 start-8 bg-white text-slate-800 rounded-xl px-4 py-3 shadow-2xl">
                  <div className="text-[10px] text-slate-500 uppercase tracking-widest">{b.card2Label}</div>
                  <div className="font-display text-lg text-[#ef4444]">{b.card2Value}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="inline-block text-[10px] tracking-[0.3em] text-[#ef4444] font-semibold mb-3 uppercase">
              {b.benefitsEyebrow}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl text-[#0891b2] leading-tight">{b.benefitsTitle}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {b.benefits.map((it, i) => {
              const Icon = BENEFIT_ICONS[i] || Check;
              return (
                <div
                  key={i}
                  data-testid={`ba-benefit-${i}`}
                  className="group bg-slate-50 hover:bg-white rounded-xl p-6 transition-all hover:shadow-lg border border-transparent hover:border-slate-200"
                >
                  <div className="w-12 h-12 rounded-lg bg-[#0891b2] group-hover:bg-[#ef4444] flex items-center justify-center mb-4 transition-colors">
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-display text-lg text-[#0891b2] mb-2">{it.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{it.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="apply" className="py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-6">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-[#0f172a] text-white p-6">
              <div className="inline-block text-[10px] tracking-[0.3em] text-red-300 font-semibold mb-2 uppercase">
                {b.formEyebrow}
              </div>
              <h2 className="font-display text-2xl">{b.formTitle}</h2>
              <p className="text-white/70 text-sm mt-1">{b.formSubtitle}</p>
            </div>
            <div className="p-6 sm:p-8 space-y-5">
              {/* Company section */}
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold pb-2 border-b border-slate-100">
                {b.section1}
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={b.companyName} required>
                  <Input required value={form.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="ba-company" className="h-11" />
                </Field>
                <Field label={b.businessType} required>
                  <Select value={form.business_type} onValueChange={(v) => set("business_type", v)}>
                    <SelectTrigger data-testid="ba-btype" className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["restaurant_group", "hotel_chain", "catering", "cloud_kitchen", "supermarket", "bakery_chain", "other"].map((k) => (
                        <SelectItem key={k} value={k}>{b.businessTypes[k]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={b.tradeLicense} required>
                  <Input required dir="ltr" value={form.trade_license} onChange={(e) => set("trade_license", e.target.value)} data-testid="ba-license" className="h-11" placeholder="CN-1234567" />
                </Field>
                <Field label={b.vatNumber}>
                  <Input dir="ltr" value={form.vat_number} onChange={(e) => set("vat_number", e.target.value)} data-testid="ba-vat" className="h-11" placeholder="100000000000003" />
                </Field>
              </div>
              <Field label={b.emirate} required>
                <Select value={form.emirate} onValueChange={(v) => set("emirate", v)}>
                  <SelectTrigger data-testid="ba-emirate" className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {EMIRATES.map((e) => <SelectItem key={e} value={e}>{t.emirates[e]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>

              {/* Contact section */}
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold pb-2 border-b border-slate-100 pt-4">
                {b.section2}
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={b.contactName} required>
                  <Input required value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} data-testid="ba-cname" className="h-11" />
                </Field>
                <Field label={b.contactRole} required>
                  <Input required value={form.contact_role} onChange={(e) => set("contact_role", e.target.value)} data-testid="ba-role" className="h-11" placeholder={b.contactRolePlaceholder} />
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={b.phone} required>
                  <Input required dir="ltr" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="ba-phone" className="h-11" placeholder="+971 4-288-2777" />
                </Field>
                <Field label={b.email} required>
                  <Input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="ba-email" className="h-11" />
                </Field>
              </div>

              {/* Commercial section */}
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold pb-2 border-b border-slate-100 pt-4">
                {b.section3}
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={b.monthlyVolume}>
                  <Select value={form.monthly_volume} onValueChange={(v) => set("monthly_volume", v)}>
                    <SelectTrigger data-testid="ba-volume" className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["under_20k", "20k_100k", "100k_500k", "over_500k"].map((k) => (
                        <SelectItem key={k} value={k}>{b.monthlyVolumes[k]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={b.creditTerms}>
                  <Select value={form.credit_terms} onValueChange={(v) => set("credit_terms", v)}>
                    <SelectTrigger data-testid="ba-credit" className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["prepaid", "net_15", "net_30", "net_60"].map((k) => (
                        <SelectItem key={k} value={k}>{b.creditTermsOptions[k]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <Field label={b.authorizedSignatory}>
                <Input value={form.authorized_signatory} onChange={(e) => set("authorized_signatory", e.target.value)} data-testid="ba-signatory" className="h-11" placeholder={b.authorizedSignatoryHint} />
              </Field>
              <Field label={b.additionalNotes}>
                <Textarea rows={3} value={form.additional_notes} onChange={(e) => set("additional_notes", e.target.value)} data-testid="ba-notes" />
              </Field>

              <div className="pt-4 border-t border-slate-100">
                <SubmitBar
                  payload={payload}
                  source="business_account"
                  submitLabel={b.submit}
                  extraData={extra}
                />
                <p className="text-xs text-slate-500 mt-3">{b.formFooter}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-slate-700">
        {label} {required && <span className="text-[#ef4444]">*</span>}
      </Label>
      {children}
    </div>
  );
}
